import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import useSEO from "../lib/useSEO";
import useReveal from "../lib/useReveal";
import Marquee from "../components/Marquee";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { IMG } from "../lib/media";
import { ORDER_ONLINE_URL } from "../lib/links";

const CATEGORY_IMAGES = {
  breakfast: IMG.breakfast_muffin,
  "smash-burgers": IMG.burger_dark,
  "crispy-chicken": IMG.chicken_burger,
  "strips-wings": IMG.wings_tenders,
  "loaded-bowls": IMG.loaded_fries,
  "meal-deals": IMG.burger_board,
  sandwiches: IMG.sandwich,
  "indian-street": IMG.indian_street,
  sides: IMG.loaded_fries,
  sauces: IMG.sauces_dips,
  "hot-counter": IMG.samosas,
  cakes: IMG.cake_slice,
  brownies: IMG.doughnuts,
  "traditional-cakes": IMG.cake_slice,
  bread: IMG.bread_artisan,
  kids: IMG.burger_board,
  platters: IMG.sandwich,
  drinks: IMG.coffee_pour,
};

const TAG_CLASS = {
  "House Favourite": "bg-[#C8102E] text-[#F7F5F0]",
  "Fresh Daily": "bg-[#1A1A1A] text-[#F7F5F0]",
  Spicy: "bg-[#8B5A2B] text-[#F7F5F0]",
  Vegetarian: "bg-[#5a7d3f] text-[#F7F5F0]",
  "Best Value": "bg-[#C8102E] text-[#F7F5F0]",
  "Beast Mode": "bg-[#1A1A1A] text-[#F7F5F0]",
};

// Categories rendered as a single tidy list-card instead of many cards.
const COMPACT_CATS = new Set(["sauces", "cakes", "traditional-cakes", "brownies", "sides", "bread", "hot-counter"]);

function CompactList({ id, items }) {
  return (
    <div
      className="bg-[#F7F5F0] rounded-2xl border border-[#E8E3DB] divide-y divide-[#E0D9CC] overflow-hidden"
      data-testid={`menu-compact-${id}`}
    >
      {items.map((it, i) => (
        <div
          key={i}
          data-testid={`menu-item-${id}-${i}`}
          className="flex items-center justify-between gap-4 px-5 py-3.5"
        >
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-display text-sm md:text-base uppercase tracking-tight leading-tight">{it.name}</span>
              {it.tags &&
                it.tags.map((t) => (
                  <span
                    key={t}
                    className={`text-[8px] tracking-[0.14em] uppercase font-semibold px-1.5 py-0.5 rounded-full ${
                      TAG_CLASS[t] || "bg-[#E8E3DB] text-[#1A1A1A]"
                    }`}
                  >
                    {t}
                  </span>
                ))}
            </div>
            {it.desc && <p className="text-xs text-[#5C5C5C] mt-0.5 leading-snug">{it.desc}</p>}
            {it.kcal ? <p className="text-[11px] text-[#9a948b] mt-0.5 font-medium">{it.kcal} kcal</p> : null}
          </div>
          <a
            href={ORDER_ONLINE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            data-testid={`menu-item-order-${id}-${i}`}
            className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24] px-3.5 py-1.5 text-[10px] font-semibold tracking-[0.12em] uppercase whitespace-nowrap transition-colors"
          >
            Order <ArrowUpRight className="h-3 w-3" />
          </a>
        </div>
      ))}
    </div>
  );
}

function AddonsBlock({ addons }) {
  return (
    <div className="mt-5 bg-[#1A1A1A] text-[#F7F5F0] rounded-2xl p-5 md:p-7" data-testid="menu-addons">
      <p className="text-[11px] tracking-[0.22em] uppercase text-[#C8102E] font-semibold">Build it your way</p>
      <p className="text-xs text-[#C9C5BE] mt-1">Pick your base, fillings & toppings — then order & pay online.</p>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
        {addons.map((a) => (
          <div key={a.label} data-testid={`menu-addon-${a.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="flex items-baseline justify-between gap-3 border-b border-[#3a3a3a] pb-1.5">
              <span className="font-display text-sm md:text-base uppercase tracking-tight">{a.label}</span>
              {a.note && (
                <span className="text-[10px] tracking-[0.14em] uppercase text-[#E2A0A8] whitespace-nowrap">{a.note}</span>
              )}
            </div>
            {a.items && a.items.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {a.items.map((item) => (
                  <span
                    key={item}
                    className="inline-flex items-center rounded-full bg-[#2a2a2a] border border-[#3a3a3a] px-2.5 py-1 text-[11px] text-[#E8E3DB]"
                  >
                    {item}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Menu() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIds, setOpenIds] = useState([]); // controlled accordion
  const ref = useReveal([categories]);
  const location = useLocation();

  useSEO({
    title: "Menu — SugaSpice Bakery & Kitchen, St Neots",
    description:
      "Explore the full SugaSpice menu: all-day breakfast, smash burgers, fried chicken, Indian street food, sandwiches, sides, hot counter and bakery in St Neots.",
    image: IMG.pastries_plate,
  });

  useEffect(() => {
    api
      .get("/menu")
      .then((r) => {
        const cats = r.data.categories || [];
        setCategories(cats);
        if (cats[0]) setOpenIds([cats[0].id]); // open first one by default
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const jumpTo = (id) => {
    if (!id) return;
    setOpenIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    // small delay so accordion can expand before scroll
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
  };

  // Open + scroll to a category when arriving via /menu#category-id
  useEffect(() => {
    if (!categories.length) return;
    const id = location.hash.replace("#", "");
    if (id && categories.some((c) => c.id === id)) jumpTo(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, location.hash]);

  return (
    <div ref={ref}>
      {/* Hero */}
      <section className="bg-[#F7F5F0] pt-12 pb-12 md:pt-16 md:pb-16">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="fade-up">
            <span className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#C8102E]">
              The Menu
            </span>
            <h1 className="mt-5 font-display text-[clamp(2.8rem,7.5vw,6.5rem)] leading-[0.9] tracking-tight uppercase">
              <span className="text-[#C8102E]">Big flavour</span>
              <br />
              all day.
            </h1>
            <p className="mt-6 max-w-2xl text-base md:text-lg text-[#3a3a3a]">
              Stacked breakfast baps, a full kitchen of smash burgers, succulent crispy fried chicken, Indian street
              food, fresh bakery & cakes, and proper coffee. Tap any category to expand.
            </p>
          </div>
        </div>
      </section>

      <Marquee
        items={[
          "Smash Burgers",
          "Crispy Dipped Wings & Strips",
          "Succulent Chicken Burgers",
          "Made to Order Sandwiches",
          "Samosa & Onion Bhajis",
          "Curry & Rice",
          "Loaded Fries",
          "All Day Breakfast Rolls",
        ]}
        theme="dark"
      />

      {/* Category jump dropdown */}
      <section className="bg-[#F7F5F0] border-b border-[#D1C9BE]/60">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-5 flex items-center gap-4">
          <span className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#5C5C5C] shrink-0">
            Jump to
          </span>
          <div className="w-full max-w-sm" data-testid="menu-jump-dropdown">
            <Select onValueChange={jumpTo}>
              <SelectTrigger className="bg-white border-[#D1C9BE] rounded-full h-11 px-5 text-sm">
                <SelectValue placeholder="Choose a category…" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`menu-jump-${c.id}`}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <span className="hidden md:inline text-xs tracking-[0.16em] uppercase text-[#5C5C5C] ml-auto">
            {categories.length} categories · {categories.reduce((s, c) => s + c.items.length, 0)} items
          </span>
          <a
            href={ORDER_ONLINE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="menu-jump-order-cta"
            className="btn-pill bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24] shrink-0 ml-auto md:ml-4"
          >
            Order Online <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* Categories as accordion */}
      <section className="py-10 md:py-14 bg-[#F7F5F0]">
        <div className="mx-auto max-w-[1100px] px-6 md:px-12">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-[#5C5C5C]">
              <Loader2 className="h-6 w-6 animate-spin mr-3" /> Loading menu…
            </div>
          ) : (
            <Accordion
              type="multiple"
              value={openIds}
              onValueChange={setOpenIds}
              className="space-y-4"
              data-testid="menu-accordion"
            >
              {categories.map((cat, idx) => (
                <AccordionItem
                  key={cat.id}
                  value={cat.id}
                  id={cat.id}
                  data-testid={`menu-cat-${cat.id}`}
                  className="bg-white rounded-3xl border border-[#D1C9BE]/60 overflow-hidden scroll-mt-24 fade-up"
                  style={{ transitionDelay: `${idx * 30}ms` }}
                >
                  <AccordionTrigger className="px-5 md:px-7 py-5 hover:no-underline gap-4">
                    <div className="flex items-center gap-4 md:gap-5 text-left flex-1">
                      <img
                        src={CATEGORY_IMAGES[cat.id] || IMG.pastries_plate}
                        alt={cat.name}
                        className="h-16 w-16 md:h-20 md:w-20 rounded-2xl object-cover shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-display text-lg md:text-2xl uppercase tracking-tight leading-tight">
                          {cat.name}
                        </h3>
                        <p className="text-xs md:text-sm text-[#5C5C5C] mt-1 line-clamp-2">{cat.blurb}</p>
                      </div>
                      <span className="hidden sm:inline-flex shrink-0 items-center px-3 py-1 rounded-full bg-[#E8E3DB] text-[10px] tracking-[0.16em] uppercase font-semibold text-[#1A1A1A]">
                        {cat.items.length} items
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 md:px-7 pb-6">
                    {COMPACT_CATS.has(cat.id) ? (
                      <CompactList id={cat.id} items={cat.items} />
                    ) : (
                      <>
                        {cat.addons && cat.addons.length > 0 && (
                          <p className="text-[11px] tracking-[0.22em] uppercase text-[#5C5C5C] font-semibold mb-3">
                            Choose your base
                          </p>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {cat.items.map((it, i) => {
                            return (
                              <div
                                key={i}
                                data-testid={`menu-item-${cat.id}-${i}`}
                                className="bg-[#F7F5F0] rounded-2xl p-5 border border-[#E8E3DB] flex flex-col gap-2"
                              >
                                <h4 className="font-display text-base md:text-lg tracking-tight uppercase leading-tight">
                                  {it.name}
                                </h4>
                                <p className="text-sm text-[#5C5C5C] leading-relaxed">{it.desc}</p>
                                {it.kcal ? <p className="text-[11px] text-[#9a948b] font-medium">{it.kcal} kcal</p> : null}
                                {it.tags && it.tags.length > 0 && (
                                  <div className="flex flex-wrap gap-1.5 mt-1">
                                    {it.tags.map((t) => (
                                      <span
                                        key={t}
                                        className={`text-[9px] tracking-[0.14em] uppercase font-semibold px-2 py-0.5 rounded-full ${
                                          TAG_CLASS[t] || "bg-[#E8E3DB] text-[#1A1A1A]"
                                        }`}
                                      >
                                        {t}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                <a
                                  href={ORDER_ONLINE_URL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`menu-item-order-${cat.id}-${i}`}
                                  className="mt-auto pt-2 inline-flex w-fit items-center gap-1 rounded-full bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24] px-4 py-1.5 text-[10px] font-semibold tracking-[0.14em] uppercase transition-colors"
                                >
                                  Order online <ArrowUpRight className="h-3 w-3" />
                                </a>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                    {cat.addons && cat.addons.length > 0 && <AddonsBlock addons={cat.addons} />}
                    <div className="mt-5 flex justify-end">
                      <a
                        href={ORDER_ONLINE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`menu-cat-order-cta-${cat.id}`}
                        className="btn-pill bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24] text-sm"
                      >
                        Order {cat.name} online <ArrowUpRight className="h-4 w-4" />
                      </a>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#1A1A1A] text-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 text-center">
          <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tight">Ready when you are.</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href={ORDER_ONLINE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-pill bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24]"
              data-testid="menu-order-cta"
            >
              Order Online <ArrowUpRight className="h-4 w-4" />
            </a>
            <Link
              to="/visit"
              className="btn-pill border-2 border-[#F7F5F0]/30 text-[#F7F5F0] hover:bg-[#F7F5F0] hover:text-[#1A1A1A]"
            >
              Visit the Bakery
            </Link>
          </div>
          <p className="mt-8 mx-auto max-w-2xl text-sm text-[#C9C5BE] leading-relaxed" data-testid="menu-allergen-notice">
            <strong className="text-[#F7F5F0]">Allergens:</strong> our food is prepared in a kitchen
            that handles nuts, gluten, milk, eggs, soya, sesame and other allergens, so we cannot
            guarantee any item is completely free from them. Allergen information for each item is
            shown during online ordering, or just ask a member of our team before you order. If you
            have a food allergy or intolerance, please tell us so we can help.
          </p>
        </div>
      </section>
    </div>
  );
}
